import {
  SignupsDto,
  StartedProductDto,
  StartedWorkspaceDto,
  StartProductDto,
  StartWorkspaceDto,
} from "@growth/dto";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useActiveStore } from "../stores/active";
import { api } from "./api";

/**
 * Whether strangers may sign up right now (the server decides). Undefined until it answers;
 * pages that offer sign-up treat that as closed.
 */
export const useSignupsOpen = (): boolean | undefined =>
  useQuery({
    queryKey: ["signups-open"],
    queryFn: async () => (await api.get<SignupsDto>("/onboarding/open")).data.open,
    staleTime: Infinity,
    retry: false,
  }).data;

/** Step 1: the person's own workspace, which becomes the active one. */
export const useStartWorkspace = () => {
  const queryClient = useQueryClient();
  const set = useActiveStore((state) => state.set);
  return useMutation({
    mutationFn: async (input: StartWorkspaceDto) =>
      (await api.post<StartedWorkspaceDto>("/onboarding/workspace", input)).data,
    onSuccess: async (workspace) => {
      set(workspace.id, null);
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
};

/** Step 2: the product and its website; the agents start on it at once. */
export const useStartProduct = () => {
  const queryClient = useQueryClient();
  const set = useActiveStore((state) => state.set);
  return useMutation({
    mutationFn: async (input: StartProductDto) =>
      (await api.post<StartedProductDto>("/onboarding/product", input)).data,
    onSuccess: async (product) => {
      set(useActiveStore.getState().workspaceId, product.brandId);
      queryClient.removeQueries({ queryKey: ["setup"] });
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
};
